// @flow

import React from 'react';
import type { Node } from 'react';
import styled from 'styled-components';

import { useImages } from 'hooks';
import { flex, h2842, Layout, Width } from 'styles';
import { ImageLoader } from 'components/Gui';
import type { AllImages } from 'hooks/use-images';
import { BREAK_MD } from 'styles/Width';

const BREAK_DESKTOP_LAYOUT = 1000;

const Wrapper = styled.section`
  ${flex()};
  flex-direction: column;
  margin-top: ${Layout.NAV_HEIGHT_MOBILE}px;

  @media print, screen and (min-width: ${Width.BREAK_MOBILE_NAV}px) {
    margin-top: 0;
  }

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
  }
`;

const Image = styled.div`
  width: 100%;

  &:nth-of-type(1) {
    display: none;
    width: 55%;
    flex: 0 0 auto;
    order: 2;

    @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
      display: block;
    }

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      width: 600px;
    }
  }

  &:nth-of-type(2) {
    @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
      display: none;
    }
  }

  > div {
    width: calc(100% + ${Width.MARGIN_SMALL}px * 2);
    transform: translateX(-${Width.MARGIN_SMALL}px);

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      width: calc(100% + ${Width.MARGIN_MEDIUM}px * 2);
      transform: translateX(-${Width.MARGIN_MEDIUM}px);
    }

    @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
      width: 70vw;
      transform: translateX(5vw);

      @media print, screen and (min-width: ${Width.BREAK_XL}px) {
        width: 850px;
        transform: none;
      }
    }
  }
`;

const Text = styled.div`
  width: 100%;
  margin-top: 32px;
  text-align: center;

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    text-align: left;
    margin-top: 100px;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 145px;
  }

  h1 {
    max-width: 325px;
    margin: 0 auto 20px auto;
    ${h2842};

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      max-width: unset;
    }

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      margin: 0 auto 30px auto;
    }
  }

  p {
    max-width: 300px;
    margin: 0 auto;

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      max-width: unset;
    }
  }
`;

const BREAK_ROW = 1100;

const Buttons = styled.div`
  margin-top: 29px;
  display: inline-flex;
  flex-direction: column;
  width: 100%;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 24px;
    width: unset;
  }

  @media print, screen and (min-width: ${BREAK_ROW}px) {
    display: flex;
    flex-direction: row;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 32px;
  }

  > div {
    margin: 0 auto 16px auto;
    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      margin: 0 0 16px 0;
    }
    @media print, screen and (min-width: ${BREAK_ROW}px) {
      margin-bottom: 0;
      margin-right: 16px;
    }
    &:last-child {
      margin-bottom: 0;
      @media print, screen and (min-width: ${BREAK_ROW}px) {
        margin-right: 0;
      }
    }
  }
`;

const BelowButtons = styled.div`
  ${flex()};
  flex-direction: column;
  margin-top: 32px;

  @media print, screen and (min-width: ${BREAK_MD}px) {
    flex-direction: row;
  }

  @media print, screen and (min-width: ${BREAK_DESKTOP_LAYOUT}px) {
    margin-top: 20px;
    justify-content: flex-start;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 34px;
  }
`;

type Props = {
  image: {
    name: {
      desktop: string,
      tablet: string,
    },
    alt: string,
  },
  text: Node,
  buttons: Node[],
  belowButtons: Node,
  images: AllImages,
};

const TabletFullWidthImageHero = ({
  image: {
    name: { desktop, tablet },
    alt,
  },
  text,
  buttons,
  belowButtons,
  images,
  ...rest
}: Props) => {
  const getImage = useImages(images);
  return (
    <Wrapper {...rest}>
      <Image>
        <div>
          <ImageLoader image={getImage(desktop)} alt={alt} overrideLoading />
        </div>
      </Image>
      <Image>
        <div>
          <ImageLoader image={getImage(tablet)} alt={alt} overrideLoading />
        </div>
      </Image>
      <Text>
        {text}
        <Buttons>{buttons}</Buttons>
        <BelowButtons>{belowButtons}</BelowButtons>
      </Text>
    </Wrapper>
  );
};

export default TabletFullWidthImageHero;
